import { differenceInDays, subDays } from "date-fns";
import firebase from "firebase/compat/app";
import { db } from "services/firebase";
import { isFirestorePermissionDenied } from "utils/firestoreErrors";
import { listActiveFdeAssignmentsForUser, listTciRegistry } from "./fdeRegistryService";
import { getTciGovernanceProfile, saveTciGovernanceProfile } from "./tciGovernanceService";

/** Firestore `in` / `not-in` disjunction limit (SDK ≥10 supports 30). */
const FIRESTORE_IN_MAX = 30;

/** Max document refs per `getAll` batch. */
const GET_ALL_MAX = 100;

/**
 * Team-activity lookback for annotation counts / quiet detection.
 * Quiet threshold remains 14 days (within this window).
 */
const TEAM_ACTIVITY_LOOKBACK_DAYS = 30;

/**
 * Per teacherUid-`in` chunk: cap how many recent annotations we read.
 * With a 30-day filter this is plenty for activity stats; overshoot is flagged.
 */
const TEAM_ANNOTATIONS_PER_CHUNK_LIMIT = 300;

/** Pending annotation / candidate queues — hard caps for dashboard lists + counts. */
const PENDING_ANNOTATIONS_LIMIT = 200;
/** Exported so ManageTcisPage can reuse the same institution-candidate cap. */
export const PENDING_CANDIDATES_LIMIT = 100;

/** Recent-activity scan for critical sessions / last-annotation fallback. */
const ANNOTATION_RECENT_SCAN_LIMIT = 100;

/** Recent candidate decisions / last promotion. */
const CANDIDATE_DECISIONS_LIMIT = 50;

/** Member detail history page (service returns array; UI can page later). */
const MEMBER_ANNOTATIONS_DEFAULT_LIMIT = 100;

/** FDE-C roster list for one institution — assignments stay small vs annotations. */
const FDE_ASSIGNMENTS_SCAN_LIMIT = 500;

function asDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value.seconds != null) return new Date(value.seconds * 1000);
  return null;
}

function chunkArray(items, size) {
  const list = Array.isArray(items) ? items : [];
  const out = [];
  for (let i = 0; i < list.length; i += size) {
    out.push(list.slice(i, i + size));
  }
  return out;
}

/**
 * Batch document gets (avoids per-id N+1). Falls back to parallel `.get()` if
 * `getAll` is unavailable (tests / older runtimes).
 */
async function getDocsByIds(collectionName, ids = []) {
  const unique = [...new Set((ids || []).filter(Boolean))];
  if (!unique.length) return [];

  const results = [];
  for (const idChunk of chunkArray(unique, GET_ALL_MAX)) {
    const refs = idChunk.map((id) => db.collection(collectionName).doc(id));
    if (typeof db.getAll === "function") {
      // eslint-disable-next-line no-await-in-loop -- sequential batches keep payload bounded
      const snaps = await db.getAll(...refs);
      results.push(...snaps);
    } else {
      // eslint-disable-next-line no-await-in-loop
      const snaps = await Promise.all(refs.map((ref) => ref.get()));
      results.push(...snaps);
    }
  }
  return results;
}

async function safeQueryGet(label, queryFactory) {
  try {
    return await queryFactory();
  } catch (err) {
    console.warn(`[tciManagerService] ${label} failed`, err);
    throw err;
  }
}

/**
 * Dual-signal loop health — mirrors functions/src/fde/loopHealth.js computeLoopStatus.
 */
export function computeLoopHealth({ daysSinceLastPromotion, daysSinceLastAnnotation }) {
  const promotionDays = daysSinceLastPromotion;
  const annotationDays = daysSinceLastAnnotation;

  if (promotionDays == null && annotationDays == null) {
    return {
      status: "stalled",
      tone: "error",
      // Spec (TciManagement.md): Needs attention when 30+ days or none.
      label: "Needs attention",
      reason: "No promotions or annotations recorded for this TCI.",
    };
  }

  const healthyPromotion = promotionDays != null && promotionDays <= 14;
  const healthyAnnotation = annotationDays != null && annotationDays <= 7;
  if (healthyPromotion && healthyAnnotation) {
    return {
      status: "healthy",
      tone: "success",
      label: "Healthy",
      reason: "Recent promotion and annotations within cadence.",
    };
  }

  const slowingPromotion = promotionDays != null && promotionDays <= 30;
  const slowingAnnotation = annotationDays != null && annotationDays <= 14;
  if (slowingPromotion || slowingAnnotation) {
    return {
      status: "slowing",
      tone: "warning",
      label: "Slowing",
      reason:
        annotationDays != null && annotationDays > 7
          ? "Annotations are getting sparse."
          : "Promotions are lagging cadence.",
    };
  }

  return {
    status: "stalled",
    tone: "error",
    label: "Needs attention",
    reason:
      annotationDays == null
        ? "No recent annotations and no recent promotion."
        : "Annotation and promotion cadence both exceeded.",
  };
}

/** @deprecated Prefer computeLoopHealth — kept for callers that only have promotion days. */
export function loopHealthFromDays(daysSincePromotion) {
  return computeLoopHealth({
    daysSinceLastPromotion: daysSincePromotion,
    daysSinceLastAnnotation: daysSincePromotion == null ? null : 0,
  });
}

function deriveConfiguringBlocker({ profile, fdeCCount, tciId }) {
  if (!profile || profile.status !== "configuring") return null;
  const passes = profile.passesCompleted || {};
  if (!passes.pass1) {
    return {
      tciId,
      nextAction: "Complete governance interrogatory pass 1 (goals & failure definition).",
      href: `/dashboard/tci-governance/new?tciId=${encodeURIComponent(tciId)}`,
    };
  }
  if (!passes.pass2) {
    return {
      tciId,
      nextAction: "Complete governance interrogatory pass 2 (FDE role discovery).",
      href: `/dashboard/tci-governance/new?tciId=${encodeURIComponent(tciId)}`,
    };
  }
  if (!passes.pass3) {
    return {
      tciId,
      nextAction: "Complete governance interrogatory pass 3 (must-do / must-not-do).",
      href: `/dashboard/tci-governance/new?tciId=${encodeURIComponent(tciId)}`,
    };
  }
  if (!fdeCCount) {
    return {
      tciId,
      nextAction: "Assign and certify at least one Clinical Annotator for this TCI.",
      href: "/dashboard/fde-roster",
    };
  }
  return {
    tciId,
    nextAction: "Finish activating this TCI governance profile.",
    href: `/dashboard/tci-governance/${encodeURIComponent(tciId)}`,
  };
}

function emptyMetricsFailure({ tciId, err, profile = null }) {
  return {
    tciId,
    metricsLoadFailed: true,
    metricsErrorMessage: err?.message || "Unable to load TCI metrics.",
    pendingAnnotations: 0,
    pendingCandidateCount: 0,
    pendingCandidates: [],
    recentDecisions: [],
    criticalSessions: [],
    configuringBlocker: null,
    fdeCCount: 0,
    criticalSinceLastReview: 0,
    lastPromotionAt: null,
    daysSinceLastAnnotation: null,
    daysSincePromotion: null,
    loopHealth: computeLoopHealth({
      daysSinceLastPromotion: null,
      daysSinceLastAnnotation: null,
    }),
    candidateNudgeThreshold: profile?.candidateNudgeThreshold ?? 2,
    reviewCadence: profile?.reviewCadence ?? "triggered",
    governanceProfile: profile,
    metricsTruncated: false,
  };
}

export async function loadManagedDomains({ uid, institutionId }) {
  if (!uid || !institutionId) return [];

  const domainIds = new Set();

  try {
    const assignments = await listActiveFdeAssignmentsForUser({ uid, institutionId });
    assignments
      .filter((row) => row.role === "fde-dc" && row.status === "active")
      .forEach((row) => {
        if (row.domainId) domainIds.add(row.domainId);
      });
  } catch (err) {
    console.warn("[loadManagedDomains] FDE-DC assignment lookup failed", err);
  }

  try {
    const managerDomainsSnap = await db
      .collection("clinicalDomains")
      .where("institutionId", "==", institutionId)
      .where("tciManagerUid", "==", uid)
      .where("isActive", "==", true)
      .get();
    managerDomainsSnap.docs.forEach((doc) => domainIds.add(doc.id));
  } catch (err) {
    console.warn("[loadManagedDomains] tciManagerUid domain lookup failed", err);
  }

  if (!domainIds.size) return [];

  try {
    const domainSnaps = await getDocsByIds("clinicalDomains", [...domainIds]);
    return domainSnaps
      .filter((snap) => snap.exists && snap.data()?.isActive !== false)
      .map((snap) => ({ id: snap.id, ...snap.data() }));
  } catch (err) {
    console.warn("[loadManagedDomains] domain doc lookup failed", err);
    return [];
  }
}

export async function fetchUserLabels(uids = []) {
  const unique = [...new Set(uids.filter(Boolean))];
  const labels = {};
  if (!unique.length) return labels;

  try {
    const snaps = await getDocsByIds("users", unique);
    snaps.forEach((snap) => {
      if (!snap?.id) return;
      if (!snap.exists) {
        labels[snap.id] = snap.id;
        return;
      }
      const data = snap.data() || {};
      labels[snap.id] =
        data.displayName ||
        data.name ||
        [data.firstName, data.lastName].filter(Boolean).join(" ") ||
        data.email ||
        snap.id;
    });
    unique.forEach((uid) => {
      if (!labels[uid]) labels[uid] = uid;
    });
  } catch (err) {
    console.warn("[fetchUserLabels] batch lookup failed; falling back per uid", err);
    await Promise.all(
      unique.map(async (uid) => {
        try {
          const snap = await db.collection("users").doc(uid).get();
          const data = snap.data() || {};
          labels[uid] =
            data.displayName ||
            data.name ||
            [data.firstName, data.lastName].filter(Boolean).join(" ") ||
            data.email ||
            uid;
        } catch (inner) {
          console.warn("[fetchUserLabels] skipped user lookup", uid, inner);
          labels[uid] = uid;
        }
      }),
    );
  }
  return labels;
}

/**
 * Load dashboard metrics for one managed TCI using scoped, bounded queries.
 * Does not read institution-wide annotation/candidate collections.
 */
async function loadTciMetrics({ institutionId, tciId }) {
  const annBase = () =>
    db
      .collection("teacherAnnotations")
      .where("institutionId", "==", institutionId)
      .where("tciId", "==", tciId);

  const candBase = () =>
    db
      .collection("promptCandidates")
      .where("institutionId", "==", institutionId)
      .where("tciId", "==", tciId);

  let pendingAnnSnap;
  let recentAnnSnap;
  let pendingCandSnap;
  let decidedCandSnap;
  let profile;

  try {
    [pendingAnnSnap, recentAnnSnap, pendingCandSnap, decidedCandSnap, profile] =
      await Promise.all([
        safeQueryGet("pendingAnnotations", () =>
          annBase()
            .where("status", "in", ["submitted", "consolidated"])
            .limit(PENDING_ANNOTATIONS_LIMIT)
            .get(),
        ),
        safeQueryGet("recentAnnotations", () =>
          annBase().orderBy("createdAt", "desc").limit(ANNOTATION_RECENT_SCAN_LIMIT).get(),
        ),
        safeQueryGet("pendingCandidates", () =>
          candBase().where("status", "==", "pending").limit(PENDING_CANDIDATES_LIMIT).get(),
        ),
        safeQueryGet("decidedCandidates", () =>
          candBase()
            .where("status", "in", ["approved", "rejected", "returned"])
            .orderBy("reviewedAt", "desc")
            .limit(CANDIDATE_DECISIONS_LIMIT)
            .get(),
        ),
        getTciGovernanceProfile({ institutionId, tciId }),
      ]);
  } catch (err) {
    console.warn("[loadTciMetrics] scoped query failed", { institutionId, tciId, err });
    if (isFirestorePermissionDenied(err)) {
      return emptyMetricsFailure({ tciId, err });
    }
    // Intentionally NO institution-wide fallback dump (was unbounded + over-scoped).
    // Retry with index-light paths for deploy lag of new composite indexes.
    try {
      [pendingAnnSnap, recentAnnSnap, pendingCandSnap, decidedCandSnap, profile] =
        await Promise.all([
          annBase().limit(PENDING_ANNOTATIONS_LIMIT).get(),
          annBase().limit(ANNOTATION_RECENT_SCAN_LIMIT).get(),
          candBase().limit(PENDING_CANDIDATES_LIMIT).get(),
          candBase().limit(CANDIDATE_DECISIONS_LIMIT).get(),
          getTciGovernanceProfile({ institutionId, tciId }),
        ]);
      // Client-side filter when status/orderBy indexes are not ready.
      pendingAnnSnap = {
        docs: pendingAnnSnap.docs.filter((doc) =>
          ["submitted", "consolidated"].includes(doc.data()?.status),
        ),
        size: 0,
      };
      pendingAnnSnap.size = pendingAnnSnap.docs.length;

      pendingCandSnap = {
        docs: pendingCandSnap.docs.filter((doc) => doc.data()?.status === "pending"),
        size: 0,
      };
      pendingCandSnap.size = pendingCandSnap.docs.length;

      decidedCandSnap = {
        docs: decidedCandSnap.docs
          .filter((doc) =>
            ["approved", "rejected", "returned"].includes(doc.data()?.status),
          )
          .sort((a, b) => {
            const ad = asDate(a.data()?.reviewedAt)?.getTime() || 0;
            const bd = asDate(b.data()?.reviewedAt)?.getTime() || 0;
            return bd - ad;
          }),
        size: 0,
      };
      decidedCandSnap.size = decidedCandSnap.docs.length;

      recentAnnSnap = {
        docs: [...recentAnnSnap.docs].sort((a, b) => {
          const ad = asDate(a.data()?.createdAt)?.getTime() || 0;
          const bd = asDate(b.data()?.createdAt)?.getTime() || 0;
          return bd - ad;
        }),
        size: recentAnnSnap.docs.length,
      };
    } catch (fallbackErr) {
      console.warn("[loadTciMetrics] bounded retry failed", {
        institutionId,
        tciId,
        fallbackErr,
      });
      profile = await getTciGovernanceProfile({ institutionId, tciId }).catch(() => null);
      return emptyMetricsFailure({ tciId, err: fallbackErr, profile });
    }
  }

  const pendingAnnotationsRaw = pendingAnnSnap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    createdAtDate: asDate(doc.data()?.createdAt),
  }));
  const recentAnnotations = recentAnnSnap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    createdAtDate: asDate(doc.data()?.createdAt),
  }));
  const pendingCandidates = pendingCandSnap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    reviewedAtDate: asDate(doc.data()?.reviewedAt),
  }));
  const decidedCandidates = decidedCandSnap.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    reviewedAtDate: asDate(doc.data()?.reviewedAt),
  }));

  const pendingAnnotationsTruncated =
    pendingAnnSnap.size >= PENDING_ANNOTATIONS_LIMIT ||
    pendingAnnotationsRaw.length >= PENDING_ANNOTATIONS_LIMIT;
  const pendingCandidatesTruncated =
    pendingCandSnap.size >= PENDING_CANDIDATES_LIMIT ||
    pendingCandidates.length >= PENDING_CANDIDATES_LIMIT;

  const pendingAnnotations = pendingAnnotationsRaw.length;
  const approved = decidedCandidates.filter((row) => row.status === "approved");
  const recentDecisions = decidedCandidates
    .slice(0, 8)
    .map((row) => ({
      id: row.id,
      status: row.status,
      promptId: row.promptId,
      reviewedAt: row.reviewedAtDate,
      reviewNotes: row.reviewNotes || null,
    }));

  const lastPromotionAt = approved[0]?.reviewedAtDate || null;
  const daysSincePromotion =
    lastPromotionAt != null ? differenceInDays(new Date(), lastPromotionAt) : null;

  const lastAnnotationAt =
    recentAnnotations.find((row) => row.createdAtDate)?.createdAtDate || null;
  const daysSinceLastAnnotation =
    lastAnnotationAt != null ? differenceInDays(new Date(), lastAnnotationAt) : null;

  const criticalSessions = recentAnnotations
    .filter((row) => {
      if (row.sessionAnnotation?.severity !== "critical") return false;
      if (!row.createdAtDate) return false;
      if (!lastPromotionAt) return true;
      return row.createdAtDate > lastPromotionAt;
    })
    .slice(0, 8)
    .map((row) => ({
      id: row.id,
      sessionId: row.sessionId,
      teacherUid: row.teacherUid,
      createdAt: row.createdAtDate,
      notes: row.sessionAnnotation?.notes || "",
    }));

  const criticalSinceLastReview = criticalSessions.length;

  let fdeCCount = 0;
  try {
    const fdeCAssignmentsSnap = await db
      .collection("fdeAssignments")
      .where("institutionId", "==", institutionId)
      .where("role", "==", "fde-c")
      .where("status", "==", "active")
      .where("tciId", "==", tciId)
      .limit(FDE_ASSIGNMENTS_SCAN_LIMIT)
      .get();
    fdeCCount = fdeCAssignmentsSnap.size;
  } catch (err) {
    console.warn("[loadTciMetrics] FDE-C count lookup failed", { institutionId, tciId, err });
  }

  let storedLoopStatus = null;
  try {
    const metricsSnap = await db
      .collection("loopHealthMetrics")
      .doc(`${institutionId}__${tciId}`)
      .get();
    if (metricsSnap.exists) {
      storedLoopStatus = metricsSnap.data()?.loopStatus || null;
    }
  } catch (err) {
    console.warn("[loadTciMetrics] loopHealthMetrics read skipped", err);
  }

  const liveLoopHealth = computeLoopHealth({
    daysSinceLastPromotion: daysSincePromotion,
    daysSinceLastAnnotation,
  });

  const STATUS_CHIP = {
    healthy: { tone: "success", label: "Healthy" },
    slowing: { tone: "warning", label: "Slowing" },
    stalled: { tone: "error", label: "Needs attention" },
  };
  const loopHealth =
    storedLoopStatus && STATUS_CHIP[storedLoopStatus]
      ? {
          status: storedLoopStatus,
          tone: STATUS_CHIP[storedLoopStatus].tone,
          label: STATUS_CHIP[storedLoopStatus].label,
          reason: liveLoopHealth.reason,
          fromStoredMetrics: true,
        }
      : liveLoopHealth;

  const configuringBlocker = deriveConfiguringBlocker({
    profile,
    fdeCCount,
    tciId,
  });

  return {
    tciId,
    pendingAnnotations,
    pendingCandidateCount: pendingCandidates.length,
    pendingCandidates,
    recentDecisions,
    criticalSessions,
    configuringBlocker,
    loopHealth,
    daysSincePromotion,
    daysSinceLastAnnotation,
    lastPromotionAt,
    criticalSinceLastReview,
    fdeCCount,
    candidateNudgeThreshold: profile?.candidateNudgeThreshold ?? 2,
    reviewCadence: profile?.reviewCadence ?? "triggered",
    governanceProfile: profile,
    metricsTruncated: pendingAnnotationsTruncated || pendingCandidatesTruncated,
    pendingAnnotationsTruncated,
    pendingCandidatesTruncated,
  };
}

export async function loadTciManagerDashboard({ uid, institutionId }) {
  const [domains, tciRegistry] = await Promise.all([
    loadManagedDomains({ uid, institutionId }),
    listTciRegistry({ institutionId }),
  ]);

  const tciNameById = Object.fromEntries(
    tciRegistry.map((row) => [row.id, row.name || row.id]),
  );

  const managedTciIds = [
    ...new Set(domains.flatMap((domain) => domain.tciIds || []).filter(Boolean)),
  ];

  // One scoped metrics load per managed TCI (domain size), not per annotation row.
  const tciMetrics = await Promise.all(
    managedTciIds.map((tciId) => loadTciMetrics({ institutionId, tciId })),
  );

  const domainIds = domains.map((domain) => domain.id);
  let team = [];
  try {
    team = await loadTeamMembers({
      institutionId,
      domainIds,
      managedTciIds,
    });
  } catch (err) {
    console.warn("[loadTciManagerDashboard] team load failed", err);
  }

  return {
    domains,
    managedTciIds,
    tciNameById,
    tciMetrics,
    team,
  };
}

/**
 * Batch-load recent annotations for many teachers (replaces per-member N+1).
 * Scoped to institutionId + teacherUid `in` chunks + createdAt lookback.
 */
async function loadRecentAnnotationsByTeachers({ institutionId, teacherUids }) {
  const byTeacher = new Map();
  const uniqueUids = [...new Set((teacherUids || []).filter(Boolean))];
  if (!uniqueUids.length) return byTeacher;

  const sinceDate = subDays(new Date(), TEAM_ACTIVITY_LOOKBACK_DAYS);
  const sinceTs = firebase.firestore.Timestamp.fromDate(sinceDate);

  for (const uidChunk of chunkArray(uniqueUids, FIRESTORE_IN_MAX)) {
    let snap;
    try {
      // Preferred: date-bounded + orderBy (composite index required).
      // eslint-disable-next-line no-await-in-loop -- sequential batches keep payload bounded
      snap = await db
        .collection("teacherAnnotations")
        .where("institutionId", "==", institutionId)
        .where("teacherUid", "in", uidChunk)
        .where("createdAt", ">=", sinceTs)
        .orderBy("createdAt", "desc")
        .limit(TEAM_ANNOTATIONS_PER_CHUNK_LIMIT)
        .get();
    } catch (err) {
      console.warn(
        "[loadTeamMembers] date-bounded annotation query failed; using capped equality query",
        err,
      );
      try {
        // Existing index: institutionId + teacherUid
        // eslint-disable-next-line no-await-in-loop -- sequential batches keep payload bounded
        snap = await db
          .collection("teacherAnnotations")
          .where("institutionId", "==", institutionId)
          .where("teacherUid", "in", uidChunk)
          .limit(TEAM_ANNOTATIONS_PER_CHUNK_LIMIT)
          .get();
      } catch (inner) {
        console.warn("[loadTeamMembers] annotation chunk failed", uidChunk, inner);
        continue;
      }
    }

    snap.docs.forEach((doc) => {
      const data = doc.data() || {};
      const createdAtDate = asDate(data.createdAt);
      if (createdAtDate && createdAtDate < sinceDate) return;
      const teacherUid = data.teacherUid;
      if (!teacherUid) return;
      if (!byTeacher.has(teacherUid)) byTeacher.set(teacherUid, []);
      byTeacher.get(teacherUid).push({
        id: doc.id,
        ...data,
        createdAtDate,
      });
    });
  }

  return byTeacher;
}

export async function loadTeamMembers({ institutionId, domainIds = [], managedTciIds = [] }) {
  const snap = await db
    .collection("fdeAssignments")
    .where("institutionId", "==", institutionId)
    .where("role", "==", "fde-c")
    .where("status", "in", ["active", "pending-training"])
    .limit(FDE_ASSIGNMENTS_SCAN_LIMIT)
    .get();

  const members = snap.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((row) => {
      if (row.domainId && domainIds.includes(row.domainId)) return true;
      if (row.tciId && managedTciIds.includes(row.tciId)) return true;
      return false;
    });

  const thirtyDaysAgo = subDays(new Date(), TEAM_ACTIVITY_LOOKBACK_DAYS);
  const quietCutoff = subDays(new Date(), 14);

  const annotationsByTeacher = await loadRecentAnnotationsByTeachers({
    institutionId,
    teacherUids: members.map((member) => member.uid),
  });

  const enriched = members.map((member) => {
    const annotations = annotationsByTeacher.get(member.uid) || [];

    const annotationCount30d = annotations.filter(
      (row) => row.createdAtDate && row.createdAtDate >= thirtyDaysAgo,
    ).length;

    const lastAnnotation = annotations
      .filter((row) => row.createdAtDate)
      .sort((a, b) => b.createdAtDate - a.createdAtDate)[0];

    const lastAnnotationDate = lastAnnotation?.createdAtDate || null;
    const isQuiet = !lastAnnotationDate || lastAnnotationDate < quietCutoff;

    let activityStatus = "active";
    if (
      member.status === "pending-training" ||
      (!member.certifiedAt && member.trainingComplete !== true)
    ) {
      activityStatus = "not-yet-certified";
    } else if (isQuiet) {
      activityStatus = "quiet";
    }

    return {
      ...member,
      annotationCount30d,
      lastAnnotationDate,
      isQuiet,
      activityStatus,
    };
  });

  const needsUserLabels = enriched.filter((member) => !member.displayName);
  const userLabels = needsUserLabels.length
    ? await fetchUserLabels(needsUserLabels.map((member) => member.uid))
    : {};
  return enriched.map((member) => ({
    ...member,
    displayName: member.displayName || userLabels[member.uid] || member.uid,
  }));
}

/**
 * Annotation history for one FDE-C. Returns most recent docs (ordered + limited).
 * Optional `limit` / `startAfterCreatedAt` support cursor-style paging without schema changes.
 */
export async function loadMemberAnnotations({
  institutionId,
  teacherUid,
  limit = MEMBER_ANNOTATIONS_DEFAULT_LIMIT,
  startAfterCreatedAt = null,
} = {}) {
  if (!institutionId || !teacherUid) return [];

  const pageSize = Math.min(Math.max(Number(limit) || MEMBER_ANNOTATIONS_DEFAULT_LIMIT, 1), 200);

  try {
    let query = db
      .collection("teacherAnnotations")
      .where("institutionId", "==", institutionId)
      .where("teacherUid", "==", teacherUid)
      .orderBy("createdAt", "desc")
      .limit(pageSize);

    if (startAfterCreatedAt) {
      const cursor =
        startAfterCreatedAt instanceof Date
          ? firebase.firestore.Timestamp.fromDate(startAfterCreatedAt)
          : startAfterCreatedAt;
      query = query.startAfter(cursor);
    }

    const snap = await query.get();
    return snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAtDate: asDate(doc.data()?.createdAt),
    }));
  } catch (err) {
    console.warn(
      "[loadMemberAnnotations] ordered query failed; falling back to capped equality query",
      err,
    );
    const snap = await db
      .collection("teacherAnnotations")
      .where("institutionId", "==", institutionId)
      .where("teacherUid", "==", teacherUid)
      .limit(pageSize)
      .get();

    return snap.docs
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAtDate: asDate(doc.data()?.createdAt),
      }))
      .sort((a, b) => (b.createdAtDate?.getTime() || 0) - (a.createdAtDate?.getTime() || 0));
  }
}

export async function loadCandidateForReview({ candidateId, institutionId, managedTciIds }) {
  const snap = await db.collection("promptCandidates").doc(candidateId).get();
  if (!snap.exists) return null;

  const candidate = { id: snap.id, ...snap.data() };
  if (candidate.institutionId !== institutionId && candidate.organizationId !== institutionId) {
    return null;
  }
  if (candidate.tciId && managedTciIds.length && !managedTciIds.includes(candidate.tciId)) {
    return null;
  }

  const annotationIds = candidate.contributingAnnotationIds?.length
    ? candidate.contributingAnnotationIds
    : candidate.sourceAnnotationId
      ? [candidate.sourceAnnotationId]
      : [];

  let annotationSnaps = [];
  try {
    annotationSnaps = await getDocsByIds("teacherAnnotations", annotationIds);
  } catch (err) {
    if (isFirestorePermissionDenied(err)) {
      console.warn("[loadCandidateForReview] annotation batch read skipped", err);
      annotationSnaps = [];
    } else {
      throw err;
    }
  }

  const annotations = annotationSnaps
    .filter((doc) => doc && doc.exists)
    .map((doc) => ({ id: doc.id, ...doc.data() }));

  const sessionIds = [...new Set(annotations.map((row) => row.sessionId).filter(Boolean))];
  let sessionSnaps = [];
  try {
    sessionSnaps = await getDocsByIds("conversationSessions", sessionIds);
  } catch (err) {
    if (isFirestorePermissionDenied(err)) {
      console.warn("[loadCandidateForReview] session batch read skipped", err);
      sessionSnaps = [];
    } else {
      throw err;
    }
  }

  const sessionDates = Object.fromEntries(
    sessionSnaps
      .filter((doc) => doc && doc.exists)
      .map((doc) => [doc.id, asDate(doc.data()?.createdAt || doc.data()?.updatedAt)]),
  );

  const userLabels = await fetchUserLabels(annotations.map((row) => row.teacherUid));

  return {
    candidate,
    annotations: annotations.map((row) => {
      const lookedUp = userLabels[row.teacherUid];
      const teacherName =
        (lookedUp && lookedUp !== row.teacherUid ? lookedUp : null) ||
        row.teacherDisplayName ||
        row.teacherName ||
        lookedUp ||
        row.teacherUid;
      return {
        ...row,
        teacherName,
        sessionDate: sessionDates[row.sessionId] || null,
      };
    }),
  };
}

export async function updateCandidateNudgeThreshold({
  institutionId,
  tciId,
  candidateNudgeThreshold,
  existingProfile,
}) {
  return saveTciGovernanceProfile({
    institutionId,
    tciId,
    ...(existingProfile || {}),
    candidateNudgeThreshold: Number(candidateNudgeThreshold),
  });
}

export async function sendTciManagerMessage({
  fromUid,
  toUid,
  institutionId,
  domainId,
  message,
}) {
  await db.collection("tciManagerMessages").add({
    fromUid,
    toUid,
    institutionId,
    domainId: domainId || null,
    message,
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    read: false,
  });
}

export async function isTciManagerForInstitution({ uid, institutionId }) {
  const assignments = await listActiveFdeAssignmentsForUser({ uid, institutionId });
  if (assignments.some((row) => row.role === "fde-dc")) return true;
  const domains = await loadManagedDomains({ uid, institutionId });
  return domains.length > 0;
}
